import { useEffect, useRef, useState } from "react";
import { Copy, Check, ArrowLeft, ExternalLink, Sparkles, Zap } from "lucide-react";
import { toast } from "sonner";
import { buildMenuEngineeringPrompt } from "../lib/menuEngineering";
import { trackEvent } from "../lib/analytics";

// Same iOS-Safari-safe copy helpers Screen3Results uses.
function legacyCopy(text) {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, 999999);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext && typeof navigator.clipboard.writeText === "function") {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  return legacyCopy(text);
}

export default function MenuEngineeringResult({ perfil, onBack }) {
  const prompt = buildMenuEngineeringPrompt(perfil);
  const promptToCopy = `${prompt}\n\nEstrategia generada con NegoIA.mx 🤖`;
  const [copied, setCopied] = useState(false);
  const resetRef = useRef(null);

  useEffect(() => {
    return () => {
      if (resetRef.current) clearTimeout(resetRef.current);
    };
  }, []);

  const handleCopy = async () => {
    const ok = await copyToClipboard(promptToCopy);
    if (ok) {
      setCopied(true);
      trackEvent("copio_prompt", { tipo: "Menu Engineering" });
      toast.success("¡Copiado! Pégalo en Claude o ChatGPT", { duration: 1500 });
      if (resetRef.current) clearTimeout(resetRef.current);
      resetRef.current = setTimeout(() => setCopied(false), 1500);
    } else {
      toast.error("No se pudo copiar. Selecciona el texto manualmente.");
    }
  };

  const openLLM = async (url) => {
    await copyToClipboard(promptToCopy);
    trackEvent("copio_prompt", { tipo: "Menu Engineering" });
    toast.success("Prompt copiado. Pégalo al abrir la pestaña.", { duration: 2000 });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="flex flex-col min-h-[100dvh] px-6 pt-10 pb-10 animate-fade-in-up"
      data-testid="menu-engineering-screen"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-orange-500 text-white shadow-md shadow-orange-500/30">
            <Sparkles className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <span className="text-lg font-bold tracking-tight text-slate-900">
            Nego<span className="text-orange-500">IA</span>
          </span>
        </div>
        <button
          type="button"
          onClick={onBack}
          data-testid="menu-engineering-back-button"
          className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 active:text-slate-800"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2.4} />
          Volver
        </button>
      </div>

      {/* Title */}
      <div className="mt-6 flex items-start gap-3">
        <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-orange-500 text-white shrink-0 shadow-md shadow-orange-500/30">
          <Zap className="w-6 h-6" strokeWidth={2.5} />
        </span>
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 leading-tight">
            ⚡ Tu prompt de Menu Engineering
          </h1>
          <p className="text-xs text-slate-500 mt-1 leading-snug">
            Cópialo y pégalo en Claude o ChatGPT
          </p>
        </div>
      </div>

      {/* Prompt box */}
      <pre
        className="mt-6 text-[0.82rem] text-slate-700 bg-slate-50 p-4 rounded-xl border-2 border-slate-100 whitespace-pre-wrap break-words font-sans leading-relaxed max-h-[55vh] overflow-y-auto"
        data-testid="menu-engineering-prompt-text"
      >
        {prompt}
      </pre>

      {/* Copy button */}
      <button
        type="button"
        onClick={handleCopy}
        data-testid="menu-engineering-copy-button"
        className={[
          "mt-5 w-full inline-flex items-center justify-center gap-2 py-4 rounded-2xl text-lg font-bold transition-all active:scale-[0.98]",
          copied
            ? "bg-teal-600 text-white shadow-lg shadow-teal-600/30"
            : "bg-orange-500 text-white shadow-lg shadow-orange-500/30 active:bg-orange-600",
        ].join(" ")}
      >
        {copied ? (
          <>
            <Check className="w-5 h-5" strokeWidth={3} />
            Copiado
          </>
        ) : (
          <>
            <Copy className="w-5 h-5" strokeWidth={2.5} />
            📋 Copiar prompt
          </>
        )}
      </button>

      <p className="text-center text-xs text-slate-500 mt-3">
        Después de copiar, ábrelo en Claude.ai o ChatGPT y pégalo
      </p>

      {/* Quick-open links */}
      <div className="mt-4 flex items-center gap-2 justify-center flex-wrap" data-testid="menu-engineering-open-row">
        <button
          type="button"
          onClick={() => openLLM("https://claude.ai/new")}
          data-testid="menu-engineering-open-claude"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-[#C26B3C] text-white active:bg-[#a85a30] active:scale-95 transition-all"
        >
          <ExternalLink className="w-3.5 h-3.5" strokeWidth={2.5} />
          Abrir Claude →
        </button>
        <button
          type="button"
          onClick={() => openLLM("https://chatgpt.com/")}
          data-testid="menu-engineering-open-chatgpt"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-emerald-600 text-white active:bg-emerald-700 active:scale-95 transition-all"
        >
          <ExternalLink className="w-3.5 h-3.5" strokeWidth={2.5} />
          Abrir ChatGPT →
        </button>
      </div>
    </div>
  );
}
